<template>
  <div class="work-order-create-form">
    <div class="quick-fill-bar">
      <div class="quick-fill-tip">可粘贴客服对话、微信聊天或电话记录，自动提取关键信息快速填单。</div>
      <el-button type="primary" plain @click="openQuickFillDialog">对话速填</el-button>
    </div>

    <el-alert
      v-if="submitErrorSummary"
      :title="submitErrorSummary"
      type="error"
      :closable="false"
      show-icon
      class="submit-error-alert"
    />

    <el-form ref="formRef" :model="form" :rules="rules" label-width="110px" class="form-grid" status-icon>
      <div class="section-title full-row">基础报单信息</div>

      <template v-if="isMaintenanceWorkOrder">
        <el-form-item label="报修类型" prop="reportType" required :error="fieldErrors.reportType" class="form-item">
          <el-input v-model="form.reportType" placeholder="如：设备类、安装类" @input="clearFieldError('reportType')" />
        </el-form-item>
        <el-form-item label="授权码/地址" prop="storeAuthCode" required :error="fieldErrors.storeAuthCode" class="form-item">
          <el-input v-model="form.storeAuthCode" placeholder="请输入授权码或地址" @input="clearFieldError('storeAuthCode')" />
        </el-form-item>
        <el-form-item label="合作连锁店" prop="cooperativeChainId" required :error="fieldErrors.cooperativeChainId" class="form-item">
          <el-select
            v-model="form.cooperativeChainId"
            filterable
            clearable
            style="width: 100%"
            placeholder="请选择合作连锁店"
            @change="clearFieldError('cooperativeChainId')"
          >
            <el-option
              v-for="item in cooperativeChains"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="联系人" prop="contact" required :error="fieldErrors.contact" class="form-item">
          <el-input v-model="form.contact" placeholder="请输入联系人姓名" @input="clearFieldError('contact')" />
        </el-form-item>
        <el-form-item label="联系电话" prop="phone" required :error="fieldErrors.phone" class="form-item">
          <el-input v-model="form.phone" placeholder="请输入联系人手机号" @input="clearFieldError('phone')" />
        </el-form-item>
      </template>
      <template v-else>
        <el-form-item label="门店标识" prop="storeAuthCode" :error="fieldErrors.storeAuthCode" class="form-item">
          <el-input v-model="form.storeAuthCode" placeholder="可选，如线体、工位或门店标识" @input="clearFieldError('storeAuthCode')" />
        </el-form-item>
        <div class="form-item production-tip-card">
          <div class="production-tip-title">生产工单特化表单</div>
          <div class="production-tip-text">生产问题无需填写联系人、联系电话、授权码/地址与合作连锁店，按产线/设备问题直接建单即可。</div>
        </div>
      </template>

      <el-form-item label="关联设备" prop="deviceArchiveId" class="form-item">
        <el-select
          v-model="form.deviceArchiveId"
          filterable
          clearable
          style="width: 100%"
          placeholder="可选，选择后自动带出设备信息"
          @change="handleDeviceChange"
        >
          <el-option
            v-for="item in deviceArchives"
            :key="item.id"
            :label="formatDeviceLabel(item)"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item :label="isProductionWorkOrder ? '机器名称' : '产品名称'" prop="productName" required :error="fieldErrors.productName" class="form-item">
        <el-input v-model="form.productName" :placeholder="isProductionWorkOrder ? '如：灌装机、骨汤机、包装机' : '如：骨汤机'" @input="clearFieldError('productName')" />
      </el-form-item>
      <el-form-item :label="isProductionWorkOrder ? '机器型号' : '产品型号'" prop="productModel" :error="fieldErrors.productModel" class="form-item">
        <el-input v-model="form.productModel" :placeholder="isProductionWorkOrder ? '请输入机器型号或版本' : '请输入产品型号或版本'" @input="clearFieldError('productModel')" />
      </el-form-item>
      <el-form-item v-if="isProductionWorkOrder" label="责任人" prop="responsiblePerson" :error="fieldErrors.responsiblePerson" class="form-item">
        <el-input v-model="form.responsiblePerson" maxlength="50" placeholder="请输入责任人" @input="clearFieldError('responsiblePerson')" />
      </el-form-item>

      <div class="section-title full-row">分类与主数据关联</div>

      <el-form-item label="一级分类" class="form-item" required :error="fieldErrors.workOrderType">
        <el-select
          v-model="form.workOrderType"
          style="width: 100%"
          placeholder="请选择一级工单分类"
          @change="handleWorkOrderTypeChange"
        >
          <el-option
            v-for="item in workOrderTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="工单分类" class="form-item" required :error="fieldErrors.primaryCategoryId">
        <el-select
          v-model="form.primaryCategoryId"
          filterable
          clearable
          style="width: 100%"
          placeholder="请选择工单分类"
          @change="clearFieldError('primaryCategoryId')"
        >
          <el-option
            v-for="item in primaryCategories"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="预关联配件" class="form-item full-row">
        <el-select
          v-model="form.partIds"
          multiple
          filterable
          clearable
          collapse-tags
          collapse-tags-tooltip
          style="width: 100%"
          placeholder="可选，预先关联可能涉及的配件"
        >
          <el-option
            v-for="item in parts"
            :key="item.id"
            :label="formatPartLabel(item)"
            :value="item.id"
          />
        </el-select>
      </el-form-item>

      <div class="section-title full-row">故障与处理信息</div>

      <el-form-item :label="isProductionWorkOrder ? '工单内容' : '问题描述'" prop="description" class="full-row form-item" required :error="fieldErrors.description">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="4"
          :placeholder="isProductionWorkOrder ? '请填写生产异常、发生工位、影响范围、已处理经过等' : '请详细填写故障现象、发生场景、已处理经过等'"
          @input="clearFieldError('description')"
        />
      </el-form-item>
      <el-form-item v-if="isMaintenanceWorkOrder" label="处理方式" prop="handlingMethod" :error="fieldErrors.handlingMethod" class="form-item">
        <el-select v-model="form.handlingMethod" clearable style="width: 100%" @change="clearFieldError('handlingMethod')">
          <el-option label="远程解决" value="远程解决" />
          <el-option label="邮寄配件/补发" value="邮寄配件/补发" />
          <el-option label="上门服务" value="上门服务" />
          <el-option label="待分析" value="待分析" />
        </el-select>
      </el-form-item>
      <el-form-item :label="isProductionWorkOrder ? '提单内容' : '服务内容'" prop="serviceContent" class="form-item" :error="fieldErrors.serviceContent">
        <el-input
          v-model="form.serviceContent"
          type="textarea"
          :rows="3"
          :placeholder="isProductionWorkOrder ? '可选，补充提报背景、产线反馈或复现说明' : '请输入当前已知服务内容或处理方案'"
          @input="clearFieldError('serviceContent')"
        />
      </el-form-item>
      <el-form-item v-if="isMaintenanceWorkOrder" label="完工备注" prop="completionRemark" class="full-row form-item" :error="fieldErrors.completionRemark">
        <el-input v-model="form.completionRemark" type="textarea" :rows="3" placeholder="如有处理结论或已确认结单信息可填写" @input="clearFieldError('completionRemark')" />
      </el-form-item>

      <div v-if="form.workOrderType === 'MAINTENANCE'" class="section-title full-row">保修判断</div>

      <template v-if="form.workOrderType === 'MAINTENANCE'">
        <div class="warranty-tip full-row">
          <span>创建工单时先确认是否在保内，首次处理会直接使用这个保修判断。</span>
        </div>
        <el-form-item label="是否在保内" prop="warrantyResult" class="form-item">
          <el-select v-model="form.warrantyResult" style="width: 100%" @change="handleWarrantyResultChange">
            <el-option label="保内" value="IN_WARRANTY" />
            <el-option label="保外" value="OUT_OF_WARRANTY" />
            <el-option label="待确认" value="UNKNOWN" />
          </el-select>
        </el-form-item>
        <el-form-item label="保修备注" prop="warrantyRemark" class="form-item">
          <el-input
            v-model="form.warrantyRemark"
            type="textarea"
            :rows="3"
            placeholder="特殊情况、待确认原因或补充说明写这里"
          />
        </el-form-item>

        <div class="section-title section-title-action full-row">
          <span>首次处理</span>
          <el-switch
            v-model="enableFirstProcess"
            inline-prompt
            active-text="开启"
            inactive-text="关闭"
          />
        </div>

        <div v-if="enableFirstProcess" class="first-process-panel full-row">
          <div class="first-process-tip">
            适用于一次即可处理完成的工单。创建成功后会自动执行：指派处理人、保存第 1 次处理、保存结算信息，并按你选择的状态更新为“处理中”或“已结单”。
          </div>

          <div class="first-process-grid">
            <div class="process-card">
              <div class="process-card-title">处理人和结果状态</div>
              <div class="process-fields">
                <div class="field-box">
                  <span class="field-label">处理人</span>
                  <el-select
                    v-model="firstProcess.assigneeId"
                    filterable
                    clearable
                    placeholder="可选，默认不指派"
                  >
                    <el-option
                      v-for="item in afterSalesAssigneeOptions"
                      :key="item.id"
                      :label="formatPersonDisplay(item)"
                      :value="item.id"
                    />
                  </el-select>
                </div>
                <div class="field-box">
                  <span class="field-label">处理后状态</span>
                  <el-radio-group v-model="firstProcess.status">
                    <el-radio-button label="PROCESSING">转处理中</el-radio-button>
                    <el-radio-button label="CLOSED">直接结单</el-radio-button>
                  </el-radio-group>
                </div>
                <div class="field-box">
                  <span class="field-label">本次处理方式</span>
                  <el-select v-model="firstProcess.followupType" placeholder="请选择本次处理方式">
                    <el-option label="电话沟通" value="PHONE" />
                    <el-option label="远程处理" value="REMOTE" />
                    <el-option label="上门处理" value="ONSITE" />
                    <el-option label="配件寄送" value="PARTS_SHIPMENT" />
                    <el-option label="客户退货" value="CUSTOMER_RETURN" />
                    <el-option label="其他" value="OTHER" />
                  </el-select>
                </div>
                <div class="field-box field-box-wide">
                  <span class="field-label">本次判断</span>
                  <el-input v-model="firstProcess.handlingDecision" placeholder="例如：电话指导恢复正常、直接更换配件后结单" />
                </div>
                <div class="field-box">
                  <span class="field-label">配件情况</span>
                  <el-select v-model="firstProcess.partUsage" placeholder="本次是否涉及配件">
                    <el-option label="未使用配件" value="NO_PART" />
                    <el-option label="已使用/准备使用配件" value="USE_PART" />
                    <el-option label="待确认" value="PENDING" />
                  </el-select>
                </div>
                <div class="field-box">
                  <span class="field-label">人工费</span>
                  <div class="inline-fields">
                    <el-switch v-model="firstProcess.hasLaborFee" active-text="有" inactive-text="无" />
                    <el-input-number
                      v-model="firstProcess.laborAmount"
                      :precision="2"
                      :min="0"
                      controls-position="right"
                      class="field-number-input"
                      :disabled="!firstProcess.hasLaborFee"
                    />
                  </div>
                </div>
                <div class="field-box">
                  <span class="field-label">{{ getExpressTrackingLabel(firstProcess.followupType) }}</span>
                  <el-input
                    v-model="firstProcess.expressTrackingNo"
                    :placeholder="getExpressTrackingPlaceholder(firstProcess.followupType)"
                  />
                </div>
                <div class="field-box">
                  <span class="field-label">{{ getExpressProofLabel(firstProcess.followupType) }}</span>
                  <div class="upload-box">
                    <el-button plain @click="triggerExpressUpload">{{ getExpressUploadButtonText(firstProcess.followupType) }}</el-button>
                    <a
                      v-if="firstProcess.expressImageUrl"
                      :href="firstProcess.expressImageUrl"
                      target="_blank"
                      rel="noreferrer"
                      class="image-link"
                    >
                      查看已上传图片
                    </a>
                    <span v-else class="hint-text">{{ getExpressHintText(firstProcess.followupType) }}</span>
                  </div>
                  <input ref="expressImageInput" type="file" accept="image/*" class="hidden-input" @change="handleExpressImageChange" />
                </div>
                <div class="field-box field-box-wide">
                  <span class="field-label">处理结果</span>
                  <el-input
                    v-model="firstProcess.stepResult"
                    type="textarea"
                    :rows="4"
                    placeholder="填写本次检查、处理动作、客户反馈、是否恢复正常等"
                  />
                </div>
              </div>
            </div>

            <div class="process-card">
              <div class="process-card-title">本次更换配件</div>
              <div class="process-fields">
                <div class="field-box field-box-wide">
                  <span class="field-label">选择配件</span>
                  <div class="part-entry-row">
                    <el-select
                      v-model="firstProcessPartDraft.partId"
                      filterable
                      clearable
                      placeholder="请选择配件"
                      class="part-select"
                    >
                      <el-option
                        v-for="item in parts"
                        :key="item.id"
                        :label="`${formatPartLabel(item)} / ${formatMoney(item.defaultUnitPrice)}`"
                        :value="item.id"
                      />
                    </el-select>
                    <el-input-number
                      v-model="firstProcessPartDraft.replacementCount"
                      :precision="0"
                      :min="1"
                      controls-position="right"
                      class="part-qty-input"
                    />
                    <el-button type="primary" plain @click="addFirstProcessPart">添加</el-button>
                  </div>
                </div>

                <el-table
                  :data="selectedFirstProcessParts"
                  border
                  stripe
                  height="220"
                  empty-text="暂无本次配件记录"
                  class="part-table"
                >
                  <el-table-column prop="standardName" label="配件" min-width="180" />
                  <el-table-column prop="replacementCount" label="数量" width="80" />
                  <el-table-column label="单价" width="110">
                    <template #default="{ row }">{{ formatMoney(row.defaultUnitPrice) }}</template>
                  </el-table-column>
                  <el-table-column label="金额" width="110">
                    <template #default="{ row }">{{ formatMoney(row.lineAmount) }}</template>
                  </el-table-column>
                  <el-table-column label="操作" width="80" fixed="right">
                    <template #default="{ row }">
                      <el-button type="danger" text @click="removeFirstProcessPart(row.partId)">移除</el-button>
                    </template>
                  </el-table-column>
                </el-table>

                <div class="readonly-summary">
                  <span class="readonly-label">当前配件金额</span>
                  <strong>{{ formatMoney(currentFirstProcessPartAmount) }}</strong>
                  <span class="money-sub">按配件默认单价 x 数量自动计算</span>
                </div>
              </div>
            </div>

            <div class="process-card process-card-wide">
              <div class="process-card-title">最终处理与结算</div>
              <div class="process-fields process-fields-grid">
                <div class="field-box">
                  <span class="field-label">最终处理方式</span>
                  <el-input v-model="firstProcess.handlingMethod" placeholder="如：远程解决、寄送配件后恢复、上门处理完成" />
                </div>
                <div class="field-box">
                  <span class="field-label">收费方式</span>
                  <el-select
                    v-model="firstProcess.chargeMode"
                    :disabled="form.warrantyResult === 'IN_WARRANTY'"
                    placeholder="请选择收费方式"
                    @change="handleFirstProcessChargeModeChange"
                  >
                    <el-option v-if="form.warrantyResult === 'IN_WARRANTY'" label="保修免费" value="WARRANTY_FREE" />
                    <template v-else>
                      <el-option label="正常收费" value="NORMAL_CHARGE" />
                      <el-option label="特殊案例" value="SPECIAL_FREE" />
                    </template>
                  </el-select>
                </div>
                <div class="readonly-summary">
                  <span class="readonly-label">保修判断</span>
                  <strong>{{ getWarrantyText(form.warrantyResult) }}</strong>
                  <span class="money-sub">如需修改请使用上方“保修判断”</span>
                </div>
                <div class="readonly-summary">
                  <span class="readonly-label">结算方式</span>
                  <strong>默认全额支付</strong>
                  <span class="money-sub">系统自动结算，无需人工核算金额</span>
                </div>
                <div class="field-box field-box-wide">
                  <span class="field-label">最终处理结果</span>
                  <el-input
                    v-model="firstProcess.processResult"
                    type="textarea"
                    :rows="3"
                    placeholder="作为这张工单的结论，例如：更换 XX 配件后恢复正常，客户确认结单"
                  />
                </div>
                <div class="field-box field-box-wide">
                  <span class="field-label">收费备注</span>
                  <el-input
                    v-model="firstProcess.chargeRemark"
                    type="textarea"
                    :rows="3"
                    :placeholder="firstProcess.chargeMode === 'SPECIAL_FREE' ? '特殊案例必须填写原因' : '少收、免收、审批说明写这里'"
                  />
                </div>
                <div class="field-box field-box-wide">
                  <span class="field-label">内部备注</span>
                  <el-input
                    v-model="firstProcess.internalNote"
                    type="textarea"
                    :rows="3"
                    placeholder="补充说明、客户反馈、后续注意事项等"
                  />
                </div>
              </div>

              <div v-if="firstProcess.status === 'CLOSED' && firstProcessMissingCloseItems.length" class="validation-list">
                <div v-for="item in firstProcessMissingCloseItems" :key="item" class="validation-item">
                  {{ item }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </el-form>

    <div class="actions">
      <el-button type="primary" :loading="submitting" @click="submit">{{ submitLabel }}</el-button>
      <el-button @click="resetForm">重置</el-button>
    </div>

    <el-dialog v-model="quickFillDialogVisible" title="对话速填" width="720px">
      <div class="quick-fill-dialog">
        <div class="quick-fill-help">
          支持从一段对话中提取手机号、联系人、授权码/地址或门店标识、设备、工单类型、工单分类、配件关键词等信息。
        </div>
        <el-input
          v-model="conversationText"
          type="textarea"
          :rows="12"
          maxlength="4000"
          show-word-limit
          placeholder="请粘贴客户对话、售后沟通记录或语音转文字内容"
        />
        <div class="quick-fill-examples">
          例如：门店反馈骨汤机 1.0 版本不开机，联系人张三，电话13800138000，地址在天河区体育西路88号，怀疑加热板损坏。
        </div>
      </div>
      <template #footer>
        <el-button @click="quickFillDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="extractConversationToForm">提取并填入表单</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { TicketsAPI } from '@/request/tickets'
import { ProductionWorkOrdersAPI } from '@/request/productionWorkOrders'
import { WorkOrderCategoriesAPI } from '@/request/workOrderCategories'
import { MasterDataAPI } from '@/request/masterData'
import { AdminTechniciansAPI } from '@/request/users'
import { useUserStore } from '@/stores/user'
import { trimText } from '@/utils/text'
import { FEATURE_PRODUCTION_ORDERS } from '@/config/featureFlags'

const workOrderTypeOptions = [
  { label: '维修工单', value: 'MAINTENANCE' },
  ...(FEATURE_PRODUCTION_ORDERS ? [{ label: '生产工单', value: 'PRODUCTION' }] : [])
]

const fieldLabelMap = {
  workOrderType: '一级分类',
  reportType: '报修类型',
  storeAuthCode: '授权码/地址',
  cooperativeChainId: '合作连锁店',
  contact: '联系人',
  phone: '联系电话',
  deviceArchiveId: '关联设备',
  productName: '产品名称',
  productModel: '产品型号',
  responsiblePerson: '责任人',
  primaryCategoryId: '工单分类',
  description: '问题描述',
  handlingMethod: '处理方式',
  serviceContent: '服务内容',
  completionRemark: '完工备注',
  warrantyResult: '是否在保内',
  warrantyRemark: '保修备注'
}

const createEmptyForm = () => ({
  workOrderType: 'MAINTENANCE',
  reportType: '设备类',
  storeAuthCode: '',
  cooperativeChainId: null,
  contact: '',
  phone: '',
  deviceArchiveId: null,
  productName: '',
  productModel: '',
  responsiblePerson: '',
  primaryCategoryId: null,
  partIds: [],
  description: '',
  handlingMethod: '',
  serviceContent: '',
  completionRemark: '',
  warrantyResult: 'UNKNOWN',
  warrantyRemark: ''
})

const createEmptyFieldErrors = () => ({
  workOrderType: '',
  reportType: '',
  storeAuthCode: '',
  cooperativeChainId: '',
  contact: '',
  phone: '',
  deviceArchiveId: '',
  productName: '',
  productModel: '',
  responsiblePerson: '',
  primaryCategoryId: '',
  description: '',
  handlingMethod: '',
  serviceContent: '',
  completionRemark: '',
  warrantyResult: '',
  warrantyRemark: ''
})

const createEmptyFirstProcess = (assigneeId = null) => ({
  assigneeId,
  status: 'CLOSED',
  followupType: 'OTHER',
  handlingDecision: '',
  partUsage: 'PENDING',
  hasLaborFee: false,
  laborAmount: 0,
  expressTrackingNo: '',
  expressImageUrl: '',
  stepResult: '',
  partItems: [],
  handlingMethod: '',
  chargeMode: '',
  receivableAmount: 0,
  receivedAmount: 0,
  processResult: '',
  chargeRemark: '',
  internalNote: ''
})

const createEmptyPartDraft = () => ({
  partId: null,
  replacementCount: 1
})

export default {
  name: 'WorkOrderCreateForm',
  props: {
    categoryScopeType: {
      type: String,
      default: ''
    },
    submitLabel: {
      type: String,
      default: '创建工单'
    },
    successMessage: {
      type: String,
      default: '工单创建成功'
    }
  },
  emits: ['created'],
  setup(props, { emit }) {
    const userStore = useUserStore()
    const formRef = ref(null)
    const expressImageInput = ref(null)
    const submitting = ref(false)
    const submitErrorSummary = ref('')
    const quickFillDialogVisible = ref(false)
    const conversationText = ref('')
    const categories = ref([])
    const cooperativeChains = ref([])
    const deviceArchives = ref([])
    const parts = ref([])
    const afterSalesUsers = ref([])
    const enableFirstProcess = ref(false)
    const form = reactive(createEmptyForm())
    const fieldErrors = reactive(createEmptyFieldErrors())
    const firstProcess = reactive(createEmptyFirstProcess(userStore.userInfo?.id || null))
    const firstProcessPartDraft = reactive(createEmptyPartDraft())
    const isProductionWorkOrder = computed(() => form.workOrderType === 'PRODUCTION')
    const isMaintenanceWorkOrder = computed(() => form.workOrderType === 'MAINTENANCE')

    const rules = computed(() => ({
      reportType: [
        {
          validator: (_, value, callback) => {
            if (isMaintenanceWorkOrder.value && !trimText(value)) {
              callback(new Error('请输入报修类型'))
              return
            }
            callback()
          },
          trigger: 'blur'
        },
        { max: 50, message: '报修类型不能超过50个字符', trigger: 'blur' }
      ],
      storeAuthCode: [
        {
          validator: (_, value, callback) => {
            if (isMaintenanceWorkOrder.value && !trimText(value)) {
              callback(new Error('请输入授权码或地址'))
              return
            }
            callback()
          },
          trigger: 'blur'
        },
        { max: 200, message: '授权码/地址不能超过200个字符', trigger: 'blur' }
      ],
      cooperativeChainId: [
        {
          validator: (_, value, callback) => {
            if (isMaintenanceWorkOrder.value && !value) {
              callback(new Error('请选择合作连锁店'))
              return
            }
            callback()
          },
          trigger: 'change'
        }
      ],
      contact: [
        {
          validator: (_, value, callback) => {
            if (isMaintenanceWorkOrder.value && !trimText(value)) {
              callback(new Error('请输入联系人'))
              return
            }
            callback()
          },
          trigger: 'blur'
        },
        { max: 50, message: '联系人不能超过50个字符', trigger: 'blur' }
      ],
      phone: [
        {
          validator: (_, value, callback) => {
            const text = trimText(value)
            if (!isMaintenanceWorkOrder.value || !text) {
              callback()
              return
            }
            if (!/^1[3-9]\d{9}$/.test(text)) {
              callback(new Error('联系电话格式不正确'))
              return
            }
            callback()
          },
          trigger: 'blur'
        }
      ],
      productName: [
        { required: true, message: isProductionWorkOrder.value ? '请输入机器名称' : '请输入产品名称', trigger: 'blur' },
        { max: 100, message: isProductionWorkOrder.value ? '机器名称不能超过100个字符' : '产品名称不能超过100个字符', trigger: 'blur' }
      ],
      productModel: [{ max: 100, message: isProductionWorkOrder.value ? '机器型号不能超过100个字符' : '产品型号不能超过100个字符', trigger: 'blur' }],
      responsiblePerson: [{ max: 50, message: '责任人不能超过50个字符', trigger: 'blur' }],
      description: [
        { required: true, message: isProductionWorkOrder.value ? '请填写工单内容' : '请填写问题描述', trigger: 'blur' },
        { max: 1000, message: isProductionWorkOrder.value ? '工单内容不能超过1000个字符' : '问题描述不能超过1000个字符', trigger: 'blur' }
      ],
      handlingMethod: [{ max: 100, message: '处理方式不能超过100个字符', trigger: 'change' }],
      serviceContent: [{ max: 1000, message: isProductionWorkOrder.value ? '提单内容不能超过1000个字符' : '服务内容不能超过1000个字符', trigger: 'blur' }],
      completionRemark: [{ max: 1000, message: '完工备注不能超过1000个字符', trigger: 'blur' }],
      warrantyRemark: [{ max: 500, message: '保修备注不能超过500个字符', trigger: 'blur' }]
    }))

    const scopedCategories = computed(() =>
      categories.value.filter((item) => item.scopeType === form.workOrderType)
    )

    const primaryCategories = computed(() => {
      const topLevel = scopedCategories.value.filter((item) => !item.parentId)
      const hasExplicitRootNode = topLevel.some((item) => item.name === '维修工单' || item.name === '生产工单')
      if (!hasExplicitRootNode) {
        return topLevel
      }
      const rootNode = topLevel.find((item) =>
        form.workOrderType === 'MAINTENANCE' ? item.name === '维修工单' : item.name === '生产工单'
      )
      if (!rootNode) {
        return topLevel
      }
      return scopedCategories.value.filter((item) => item.parentId === rootNode.id)
    })

    const selectedDevice = computed(() => deviceArchives.value.find((item) => item.id === form.deviceArchiveId) || null)

    const afterSalesAssigneeOptions = computed(() => {
      const seen = new Set()
      const list = []
      const push = (item) => {
        if (!item?.id || seen.has(item.id)) return
        seen.add(item.id)
        list.push(item)
      }
      if (userStore.userInfo?.id) {
        push({
          id: userStore.userInfo.id,
          username: userStore.userInfo.username,
          displayName: userStore.userInfo.displayName
        })
      }
      afterSalesUsers.value.forEach(push)
      return list
    })

    const selectedFirstProcessParts = computed(() => firstProcess.partItems.map((item) => {
      const part = parts.value.find((candidate) => candidate.id === item.partId) || {}
      const price = Number(part.defaultUnitPrice || 0)
      return {
        ...part,
        partId: item.partId,
        replacementCount: Number(item.replacementCount || 1),
        defaultUnitPrice: price,
        lineAmount: Number((price * Number(item.replacementCount || 1)).toFixed(2))
      }
    }))

    const currentFirstProcessPartAmount = computed(() => selectedFirstProcessParts.value.reduce((sum, item) => {
      return Number((sum + Number(item.lineAmount || 0)).toFixed(2))
    }, 0))

    const currentFirstProcessOutstandingAmount = computed(() => {
      if (firstProcess.chargeMode === 'WARRANTY_FREE' || firstProcess.chargeMode === 'SPECIAL_FREE') {
        return 0
      }
      const receivable = Number(firstProcess.receivableAmount || 0)
      const received = Number(firstProcess.receivedAmount || 0)
      return Math.max(0, Number((receivable - received).toFixed(2)))
    })

    const firstProcessMissingCloseItems = computed(() => {
      if (!enableFirstProcess.value || !isMaintenanceWorkOrder.value) return []
      const items = []
      if (!form.warrantyResult || form.warrantyResult === 'UNKNOWN') items.push('请先确认保内或保外')
      if (!trimText(firstProcess.handlingDecision)) items.push('请填写本次处理判断')
      if (!trimText(firstProcess.stepResult)) items.push('请填写本次处理结果')
      if (!trimText(firstProcess.handlingMethod)) items.push('请填写最终处理方式')
      if (!trimText(firstProcess.processResult)) items.push('请填写最终处理结果')
      if (!firstProcess.chargeMode) items.push('请确认收费方式')
      if (isShipmentOrReturnType(firstProcess.followupType) && !firstProcess.expressImageUrl) {
        items.push(firstProcess.followupType === 'CUSTOMER_RETURN' ? '客户退货时请上传退货凭证' : '配件寄送时请上传快递凭证')
      }
      if (firstProcess.chargeMode === 'WARRANTY_FREE' && form.warrantyResult !== 'IN_WARRANTY') {
        items.push('保修免费仅适用于保内工单')
      }
      if (firstProcess.chargeMode === 'SPECIAL_FREE' && !trimText(firstProcess.chargeRemark)) {
        items.push('特殊案例必须填写收费备注')
      }
      return items
    })

    const clearFieldError = (field) => {
      if (field in fieldErrors) {
        fieldErrors[field] = ''
      }
      submitErrorSummary.value = ''
    }

    const clearAllFieldErrors = () => {
      Object.keys(fieldErrors).forEach((key) => {
        fieldErrors[key] = ''
      })
      submitErrorSummary.value = ''
    }

    const applyValidationErrors = (errors = []) => {
      if (!Array.isArray(errors) || errors.length === 0) {
        return false
      }

      clearAllFieldErrors()
      const summaryList = []
      let firstField = ''

      errors.forEach((item) => {
        const field = item?.field
        const message = item?.message || '格式不正确'
        const label = fieldLabelMap[field] || field || '字段'
        summaryList.push(`${label}：${message}`)

        if (field && field in fieldErrors && !fieldErrors[field]) {
          fieldErrors[field] = message
          if (!firstField) {
            firstField = field
          }
        }
      })

      submitErrorSummary.value = Array.from(new Set(summaryList)).slice(0, 4).join('；')
      if (firstField) {
        formRef.value?.scrollToField?.(firstField)
      }
      return true
    }

    const fetchCategories = async () => {
      const list = await WorkOrderCategoriesAPI.list({ enabledOnly: true })
      categories.value = Array.isArray(list) ? list : []
    }

    const fetchMasterData = async () => {
      const [chains, devices, partList] = await Promise.all([
        MasterDataAPI.getCooperativeChains(),
        MasterDataAPI.getDeviceArchives(),
        MasterDataAPI.getParts()
      ])
      cooperativeChains.value = Array.isArray(chains) ? chains.filter((item) => item.enabled !== false) : []
      deviceArchives.value = Array.isArray(devices) ? devices : []
      parts.value = Array.isArray(partList) ? partList.filter((item) => item.enabled !== false) : []
    }

    const fetchAfterSalesUsers = async () => {
      const list = await AdminTechniciansAPI.list('', 'ROLE_AFTER_SALES')
      afterSalesUsers.value = Array.isArray(list) ? list : []
    }

    const resetFirstProcess = () => {
      Object.assign(firstProcess, createEmptyFirstProcess(userStore.userInfo?.id || null))
      Object.assign(firstProcessPartDraft, createEmptyPartDraft())
    }

    const syncFirstProcessReceivableWithBase = () => {
      if (firstProcess.chargeMode !== 'NORMAL_CHARGE') return
      const laborAmount = firstProcess.hasLaborFee ? Number(firstProcess.laborAmount || 0) : 0
      firstProcess.receivableAmount = Number((currentFirstProcessPartAmount.value + laborAmount).toFixed(2))
    }

    const handleFirstProcessChargeModeChange = (value) => {
      if (value === 'WARRANTY_FREE' || value === 'SPECIAL_FREE') {
        firstProcess.receivableAmount = 0
        firstProcess.receivedAmount = 0
        return
      }
      syncFirstProcessReceivableWithBase()
    }

    const handleWarrantyResultChange = (value) => {
      if (!enableFirstProcess.value) {
        return
      }
      if (value === 'IN_WARRANTY') {
        firstProcess.chargeMode = 'WARRANTY_FREE'
        firstProcess.receivableAmount = 0
        firstProcess.receivedAmount = 0
        return
      }
      if (value === 'OUT_OF_WARRANTY') {
        if (!firstProcess.chargeMode || firstProcess.chargeMode === 'WARRANTY_FREE') {
          firstProcess.chargeMode = 'NORMAL_CHARGE'
        }
        syncFirstProcessReceivableWithBase()
        return
      }
      firstProcess.chargeMode = ''
      firstProcess.receivableAmount = 0
      firstProcess.receivedAmount = 0
    }

    const handleWorkOrderTypeChange = () => {
      clearFieldError('workOrderType')
      form.primaryCategoryId = null
      if (form.workOrderType === 'PRODUCTION') {
        form.reportType = '生产类'
        form.storeAuthCode = ''
        form.cooperativeChainId = null
        form.contact = ''
        form.phone = ''
        form.handlingMethod = ''
        form.completionRemark = ''
        enableFirstProcess.value = false
        resetFirstProcess()
      } else if (!form.reportType || form.reportType === '生产类') {
        form.reportType = '设备类'
      }
      ;['primaryCategoryId', 'reportType', 'storeAuthCode', 'cooperativeChainId', 'contact', 'phone', 'handlingMethod', 'completionRemark'].forEach(clearFieldError)
    }

    const handleDeviceChange = () => {
      clearFieldError('deviceArchiveId')
      const device = selectedDevice.value
      if (!device) {
        return
      }
      form.productName = device.deviceName || ''
      if (device.deviceVersion) {
        form.productModel = device.deviceVersion
      }
      clearFieldError('productName')
      clearFieldError('productModel')
      if (!form.cooperativeChainId && device.cooperativeChainId) {
        form.cooperativeChainId = device.cooperativeChainId
      }
    }

    const validateCategorySelection = () => {
      const errors = []
      if (!form.workOrderType) {
        errors.push({ field: 'workOrderType', message: '请选择一级工单分类' })
      }
      if (!form.primaryCategoryId) {
        errors.push({ field: 'primaryCategoryId', message: '请选择工单分类' })
      }
      if (errors.length > 0) {
        applyValidationErrors(errors)
        return false
      }
      return true
    }

    const formatDeviceLabel = (item) => {
      const pieces = [item.deviceName, item.warrantyRemark, item.description].filter(Boolean)
      return pieces.join(' / ') || `设备 ${item.id}`
    }

    const formatPartLabel = (item) => {
      const pieces = [item.standardName, item.brandName, item.partModel, item.versionNo].filter(Boolean)
      return pieces.join(' / ') || `配件 ${item.id}`
    }

    const formatMoney = (value) => {
      const amount = Number(value || 0)
      return Number.isFinite(amount) ? `${amount.toFixed(2)} 元` : '0.00 元'
    }

    const formatPersonDisplay = (person, emptyText = '未指派') => {
      if (!person) return emptyText
      const username = trimText(person.username)
      const displayName = trimText(person.displayName)
      if (username && displayName && username !== displayName) {
        return `${username} ${displayName}`
      }
      return username || displayName || emptyText
    }

    const getWarrantyText = (value) => ({
      IN_WARRANTY: '保内',
      OUT_OF_WARRANTY: '保外',
      UNKNOWN: '待确认'
    }[value] || '待确认')

    const openQuickFillDialog = () => {
      quickFillDialogVisible.value = true
    }

    const normalizeText = (value) => trimText(String(value || '').replace(/\r/g, ''))

    const detectWorkOrderType = (text) => {
      const lower = text.toLowerCase()
      const productionKeywords = ['生产', '出厂', '车间', '产线', '线体', '工位', '装配', '组装', '质检']
      return productionKeywords.some((item) => lower.includes(item)) ? 'PRODUCTION' : 'MAINTENANCE'
    }

    const scoreByKeywords = (text, keywords = []) => {
      return keywords.reduce((score, keyword) => {
        if (!keyword) return score
        return text.includes(String(keyword).toLowerCase()) ? score + String(keyword).length : score
      }, 0)
    }

    const findBestDevice = (text) => {
      let best = null
      let bestScore = 0
      deviceArchives.value.forEach((item) => {
        const score = scoreByKeywords(text, [item.deviceName, item.warrantyRemark, item.description])
        if (score > bestScore) {
          best = item
          bestScore = score
        }
      })
      return bestScore > 0 ? best : null
    }

    const findBestChain = (text) => {
      let best = null
      let bestScore = 0
      cooperativeChains.value.forEach((item) => {
        const score = scoreByKeywords(text, [item.name, item.code])
        if (score > bestScore) {
          best = item
          bestScore = score
        }
      })
      return bestScore > 0 ? best : null
    }

    const findBestCategory = (text, workOrderType) => {
      let best = null
      let bestScore = 0
      const candidates = primaryCategories.value.filter((item) => item.scopeType === workOrderType)
      candidates.forEach((item) => {
        const tokens = String(item.name || '')
          .split(/[\/、,，\s()（）-]+/)
          .filter((token) => token.length >= 2)
        const score = scoreByKeywords(text, [item.name, item.code, ...tokens])
        if (score > bestScore) {
          best = item
          bestScore = score
        }
      })
      return bestScore > 0 ? best : null
    }

    const findMatchedPartIds = (text) => {
      const matched = []
      parts.value.forEach((item) => {
        const score = scoreByKeywords(text, [item.standardName, item.aliasName, item.brandName, item.partModel, item.versionNo])
        if (score > 0) {
          matched.push({ id: item.id, score })
        }
      })
      return matched
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map((item) => item.id)
    }

    const detectPhone = (text) => {
      const match = text.match(/1[3-9]\d{9}/)
      return match ? match[0] : ''
    }

    const detectContact = (text) => {
      const patterns = [
        /(?:联系人|联系人是|客户是|姓名|叫|我是)[:：\s]*([A-Za-z\u4e00-\u9fa5]{2,12})/,
        /([A-Za-z\u4e00-\u9fa5]{2,12})[，,\s]*1[3-9]\d{9}/
      ]
      for (const pattern of patterns) {
        const match = text.match(pattern)
        if (match?.[1]) {
          return match[1]
        }
      }
      return ''
    }

    const detectStoreAuthCode = (text) => {
      const patterns = [
        /(?:授权码\/地址|授权码|门店地址|店址|地址)[:：\s]*([^\n，。,；;]+)/,
        /([^\n，。,；;]{4,}[路街道巷号栋楼室])/,
        /(?:门店|店里)[:：\s]*([^\n，。,；;]{2,30})/
      ]
      for (const pattern of patterns) {
        const match = text.match(pattern)
        if (match?.[1]) {
          return trimText(match[1])
        }
      }
      return ''
    }

    const detectHandlingMethod = (text) => {
      if (/(上门|到店|现场)/.test(text)) return '上门服务'
      if (/(邮寄|补发|快递|物流|发配件)/.test(text)) return '邮寄配件/补发'
      if (/(远程|电话指导|视频指导|线上处理)/.test(text)) return '远程解决'
      return ''
    }

    const detectReportType = (text, workOrderType) => {
      if (workOrderType === 'PRODUCTION') return '生产类'
      if (/(安装|新装)/.test(text)) return '安装类'
      if (/(设备|机器|不开机|故障|维修)/.test(text)) return '设备类'
      return form.reportType || '设备类'
    }

    const clearRelevantErrors = () => {
      [
        'workOrderType',
        'reportType',
        'storeAuthCode',
        'cooperativeChainId',
        'contact',
        'phone',
        'deviceArchiveId',
        'productName',
        'productModel',
        'responsiblePerson',
        'primaryCategoryId',
        'description',
        'handlingMethod',
        'serviceContent'
      ].forEach(clearFieldError)
    }

    const extractConversationToForm = () => {
      const rawText = normalizeText(conversationText.value)
      if (!rawText) {
        ElMessage.warning('请先粘贴一段对话内容')
        return
      }

      const normalizedText = rawText.toLowerCase()
      const workOrderType = detectWorkOrderType(normalizedText)
      const matchedDevice = findBestDevice(normalizedText)
      const matchedChain = findBestChain(normalizedText)
      const matchedCategory = findBestCategory(normalizedText, workOrderType)
      const matchedPartIds = findMatchedPartIds(normalizedText)
      const phone = detectPhone(rawText)
      const contact = detectContact(rawText)
      const storeAuthCode = detectStoreAuthCode(rawText)
      const handlingMethod = detectHandlingMethod(rawText)
      const reportType = detectReportType(rawText, workOrderType)
      form.workOrderType = workOrderType
      form.reportType = reportType
      form.primaryCategoryId = matchedCategory?.id || null
      form.deviceArchiveId = matchedDevice?.id || null
      form.cooperativeChainId = matchedChain?.id || matchedDevice?.cooperativeChainId || form.cooperativeChainId
      if (workOrderType === 'PRODUCTION') {
        form.phone = ''
        form.contact = ''
        form.storeAuthCode = storeAuthCode || ''
        form.cooperativeChainId = null
        enableFirstProcess.value = false
        resetFirstProcess()
      } else {
        form.phone = phone || form.phone
        form.contact = contact || form.contact
        form.storeAuthCode = storeAuthCode || form.storeAuthCode
      }
      form.productName = matchedDevice?.deviceName || form.productName
      form.productModel = matchedDevice?.deviceVersion || form.productModel
      form.partIds = matchedPartIds.length > 0 ? matchedPartIds : form.partIds
      form.handlingMethod = handlingMethod || form.handlingMethod
      form.description = rawText
      if (!form.serviceContent) {
        form.serviceContent = rawText
      }

      clearRelevantErrors()
      quickFillDialogVisible.value = false
      ElMessage.success('已提取并填入表单，请核对无误后再创建工单')
    }

    const addFirstProcessPart = () => {
      if (!firstProcessPartDraft.partId) {
        ElMessage.warning('请先选择配件')
        return
      }
      const count = Number(firstProcessPartDraft.replacementCount || 1)
      if (count <= 0) {
        ElMessage.warning('配件数量必须大于 0')
        return
      }
      const existing = firstProcess.partItems.find((item) => item.partId === firstProcessPartDraft.partId)
      if (existing) {
        existing.replacementCount = Number(existing.replacementCount || 0) + count
      } else {
        firstProcess.partItems.push({
          partId: firstProcessPartDraft.partId,
          replacementCount: count
        })
      }
      Object.assign(firstProcessPartDraft, createEmptyPartDraft())
      if (firstProcess.partItems.length > 0 && firstProcess.partUsage === 'NO_PART') {
        firstProcess.partUsage = 'USE_PART'
      }
      syncFirstProcessReceivableWithBase()
    }

    const removeFirstProcessPart = (partId) => {
      firstProcess.partItems = firstProcess.partItems.filter((item) => item.partId !== partId)
      if (firstProcess.partItems.length === 0 && firstProcess.partUsage === 'USE_PART') {
        firstProcess.partUsage = 'NO_PART'
      }
      syncFirstProcessReceivableWithBase()
    }

    const triggerExpressUpload = () => {
      expressImageInput.value?.click()
    }

    const handleExpressImageChange = async (event) => {
      const file = event?.target?.files?.[0]
      if (!file) return
      try {
        const result = await TicketsAPI.uploadFollowupImage(file)
        firstProcess.expressImageUrl = result?.url || result?.relativeUrl || ''
        ElMessage.success('凭证图片上传成功')
      } catch (error) {
        ElMessage.error(error?.message || '凭证图片上传失败')
      } finally {
        if (event?.target) {
          event.target.value = ''
        }
      }
    }

    const getFollowupTypeText = (value) => ({
      PHONE: '电话沟通',
      REMOTE: '远程处理',
      ONSITE: '上门处理',
      PARTS_SHIPMENT: '配件寄送',
      CUSTOMER_RETURN: '客户退货',
      OTHER: '其他'
    }[value] || '其他')

    const isShipmentOrReturnType = (value) => value === 'PARTS_SHIPMENT' || value === 'CUSTOMER_RETURN'

    const getExpressTrackingLabel = (value) => {
      if (value === 'CUSTOMER_RETURN') return '退货单号'
      return '快递单号'
    }

    const getExpressTrackingPlaceholder = (value) => {
      if (value === 'CUSTOMER_RETURN') return '退货单号，没有可不填'
      return '快递单号，没有可不填'
    }

    const getExpressProofLabel = (value) => {
      if (value === 'CUSTOMER_RETURN') return '退货凭证'
      return '快递凭证'
    }

    const getExpressUploadButtonText = (value) => {
      if (value === 'CUSTOMER_RETURN') return '上传退货图片'
      return '上传图片'
    }

    const getExpressHintText = (value) => {
      if (value === 'CUSTOMER_RETURN') return '客户退货时建议上传退货凭证'
      if (value === 'PARTS_SHIPMENT') return '配件寄送时建议上传凭证'
      return '有凭证可上传，便于后续核对'
    }

    const buildFirstProcessSummary = () => {
      return [
        '第1次处理',
        `处理方式：${getFollowupTypeText(firstProcess.followupType)}`,
        `保修：${getWarrantyText(form.warrantyResult)}`,
        `处理判断：${trimText(firstProcess.handlingDecision) || '未填写'}`,
        `配件情况：${({
          NO_PART: '未使用配件',
          USE_PART: '已使用/准备使用配件',
          PENDING: '待确认'
        }[firstProcess.partUsage] || '待确认')}`,
        `人工费：${firstProcess.hasLaborFee ? formatMoney(firstProcess.laborAmount) : '无'}`,
        `${getExpressTrackingLabel(firstProcess.followupType)}：${trimText(firstProcess.expressTrackingNo) || '无'}`,
        `处理结果：${trimText(firstProcess.stepResult) || '未填写'}`
      ].join('；')
    }

    const validateFirstProcess = () => {
      if (!enableFirstProcess.value || !isMaintenanceWorkOrder.value) {
        return true
      }
      if (!trimText(firstProcess.handlingDecision)) {
        ElMessage.warning('请填写本次处理判断')
        return false
      }
      if (!trimText(firstProcess.stepResult)) {
        ElMessage.warning('请填写本次处理结果')
        return false
      }
      if (isShipmentOrReturnType(firstProcess.followupType) && !firstProcess.expressImageUrl) {
        ElMessage.warning(firstProcess.followupType === 'CUSTOMER_RETURN' ? '客户退货时请先上传退货凭证' : '配件寄送时请先上传快递凭证')
        return false
      }
      if (firstProcess.partUsage === 'USE_PART' && firstProcess.partItems.length === 0) {
        ElMessage.warning('已选择涉及配件，请至少添加一条配件记录')
        return false
      }
      if (firstProcess.status === 'CLOSED' && firstProcessMissingCloseItems.value.length) {
        ElMessage.warning(firstProcessMissingCloseItems.value[0])
        return false
      }
      return true
    }

    const runImmediateFirstProcess = async (createdTicket) => {
      const ticketId = createdTicket?.id
      if (!ticketId) {
        throw new Error('工单创建成功，但未返回工单ID')
      }

      if (firstProcess.assigneeId) {
        await TicketsAPI.assign(ticketId, firstProcess.assigneeId)
      }

      const followup = await TicketsAPI.addFollowup(ticketId, {
        followupType: firstProcess.followupType,
        followupResult: buildFirstProcessSummary(),
        warrantyResult: form.warrantyResult || 'UNKNOWN',
        hasLaborFee: firstProcess.hasLaborFee,
        laborAmount: firstProcess.hasLaborFee ? Number(firstProcess.laborAmount || 0) : 0,
        expressTrackingNo: trimText(firstProcess.expressTrackingNo) || null,
        expressImageUrl: firstProcess.expressImageUrl || null
      })

      if (!followup?.id && firstProcess.partItems.length > 0) {
        throw new Error('首次处理已保存，但未返回处理记录ID，无法关联配件')
      }

      for (const item of firstProcess.partItems) {
        await TicketsAPI.addPartReplacement(ticketId, {
          followupLogId: followup.id,
          partId: item.partId,
          replacementCount: Number(item.replacementCount || 1)
        })
      }

      await TicketsAPI.adminUpdate(ticketId, {
        handlingMethod: trimText(firstProcess.handlingMethod) || trimText(form.handlingMethod) || null,
        processResult: trimText(firstProcess.processResult) || null,
        completionRemark: trimText(firstProcess.internalNote) || trimText(form.completionRemark) || null,
        warrantyResult: form.warrantyResult || null,
        warrantyRemark: trimText(form.warrantyRemark) || null,
        laborAmount: firstProcess.hasLaborFee ? Number(firstProcess.laborAmount || 0) : 0,
        receivableAmount: firstProcess.chargeMode === 'NORMAL_CHARGE' ? Number(firstProcess.receivableAmount || 0) : 0,
        receivedAmount: firstProcess.chargeMode === 'NORMAL_CHARGE' ? Number(firstProcess.receivedAmount || 0) : 0,
        chargeMode: firstProcess.chargeMode || null,
        chargeRemark: trimText(firstProcess.chargeRemark) || null
      })

      await TicketsAPI.updateStatus(ticketId, firstProcess.status)
    }

    const submit = async () => {
      clearAllFieldErrors()

      try {
        await formRef.value?.validate()
      } catch (_) {
        ElMessage.warning('请先修正表单中的必填项和格式')
        return
      }

      if (!validateCategorySelection()) {
        ElMessage.warning(submitErrorSummary.value || '请先选择完整的工单分类')
        return
      }

      if (!validateFirstProcess()) {
        return
      }

      try {
        submitting.value = true
        const categoryIds = [form.primaryCategoryId].filter(Boolean)
        let created

        if (form.workOrderType === 'PRODUCTION') {
          const categoryName = primaryCategories.value.find((item) => item.id === form.primaryCategoryId)?.name || ''
          const title = [trimText(form.productName), categoryName, trimText(form.storeAuthCode)].filter(Boolean).join(' / ') || '生产工单'
          created = await ProductionWorkOrdersAPI.create({
            title,
            categoryId: form.primaryCategoryId,
            deviceArchiveId: form.deviceArchiveId || null,
            storeIdentifier: trimText(form.storeAuthCode) || null,
            machineName: trimText(form.productName) || null,
            machineModel: trimText(form.productModel) || null,
            responsiblePerson: trimText(form.responsiblePerson) || null,
            issueContent: trimText(form.description),
            reportContent: trimText(form.serviceContent) || null,
            status: 'PENDING'
          })
        } else {
          const payload = {
            reportType: trimText(form.reportType),
            storeAuthCode: trimText(form.storeAuthCode) || null,
            cooperativeChainId: form.cooperativeChainId || null,
            contact: trimText(form.contact),
            phone: trimText(form.phone),
            deviceArchiveId: form.deviceArchiveId || null,
            productName: trimText(form.productName),
            productModel: trimText(form.productModel) || null,
            partIds: Array.isArray(form.partIds) ? form.partIds : [],
            description: trimText(form.description),
            handlingMethod: trimText(form.handlingMethod) || null,
            serviceContent: trimText(form.serviceContent) || null,
            completionRemark: trimText(form.completionRemark) || null,
            warrantyResult: form.warrantyResult || 'UNKNOWN',
            warrantyRemark: trimText(form.warrantyRemark) || null,
            categoryIds
          }
          created = await TicketsAPI.create({ ...payload, sourceType: 'AFTER_SALES' })
          if (enableFirstProcess.value) {
            await runImmediateFirstProcess(created)
          }
        }

        const successText = enableFirstProcess.value && isMaintenanceWorkOrder.value
          ? (firstProcess.status === 'CLOSED' ? '工单已创建并完成首次处理结单' : '工单已创建并记录首次处理')
          : props.successMessage
        ElMessage.success(successText)
        emit('created', created)
        resetForm()
      } catch (err) {
        if (applyValidationErrors(err?.validationErrors)) {
          ElMessage.error(submitErrorSummary.value || '提交数据校验失败，请检查表单填写')
          return
        }
        ElMessage.error(err?.message || '创建工单失败')
      } finally {
        submitting.value = false
      }
    }

    const resetForm = () => {
      Object.assign(form, createEmptyForm())
      enableFirstProcess.value = false
      resetFirstProcess()
      clearAllFieldErrors()
      formRef.value?.clearValidate?.()
    }

    watch(enableFirstProcess, (enabled) => {
      if (!enabled) {
        resetFirstProcess()
        return
      }
      if (!firstProcess.assigneeId && userStore.userInfo?.id) {
        firstProcess.assigneeId = userStore.userInfo.id
      }
      handleWarrantyResultChange(form.warrantyResult)
    })

    watch(
      () => [firstProcess.hasLaborFee, firstProcess.laborAmount, currentFirstProcessPartAmount.value, firstProcess.chargeMode],
      () => {
        syncFirstProcessReceivableWithBase()
      }
    )

    onMounted(async () => {
      try {
        await Promise.all([fetchCategories(), fetchMasterData(), fetchAfterSalesUsers()])
      } catch (err) {
        ElMessage.error(err?.message || '初始化创建工单表单失败')
      }
    })

    return {
      formRef,
      expressImageInput,
      submitting,
      submitErrorSummary,
      quickFillDialogVisible,
      conversationText,
      cooperativeChains,
      deviceArchives,
      parts,
      workOrderTypeOptions,
      isProductionWorkOrder,
      isMaintenanceWorkOrder,
      primaryCategories,
      form,
      rules,
      fieldErrors,
      enableFirstProcess,
      firstProcess,
      firstProcessPartDraft,
      afterSalesAssigneeOptions,
      selectedFirstProcessParts,
      currentFirstProcessPartAmount,
      currentFirstProcessOutstandingAmount,
      firstProcessMissingCloseItems,
      clearFieldError,
      handleWorkOrderTypeChange,
      handleDeviceChange,
      handleWarrantyResultChange,
      handleFirstProcessChargeModeChange,
      formatDeviceLabel,
      formatPartLabel,
      formatMoney,
      formatPersonDisplay,
      getWarrantyText,
      getExpressTrackingLabel,
      getExpressTrackingPlaceholder,
      getExpressProofLabel,
      getExpressUploadButtonText,
      getExpressHintText,
      openQuickFillDialog,
      extractConversationToForm,
      addFirstProcessPart,
      removeFirstProcessPart,
      triggerExpressUpload,
      handleExpressImageChange,
      submit,
      resetForm,
      submitLabel: props.submitLabel
    }
  }
}
</script>

<style scoped>
.work-order-create-form {
  padding: 8px;
}

.quick-fill-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
}

.quick-fill-tip {
  color: #606266;
  font-size: 13px;
}

.submit-error-alert {
  margin-bottom: 16px;
}

.quick-fill-dialog {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quick-fill-help {
  color: #606266;
  line-height: 1.6;
  font-size: 13px;
}

.quick-fill-examples {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 4px 0 12px;
  padding-left: 10px;
  border-left: 3px solid #409eff;
}

.section-title-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.form-item {
  margin-bottom: 16px;
}

.production-tip-card {
  min-height: 78px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f4f8ff;
  border: 1px solid #d9ecff;
  box-sizing: border-box;
}

.production-tip-title {
  color: #303133;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

.production-tip-text {
  color: #606266;
  font-size: 12px;
  line-height: 1.6;
}

.full-row {
  grid-column: 1 / -1;
}

.warranty-tip {
  padding: 10px 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: #f4f8ff;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.first-process-panel {
  display: grid;
  gap: 16px;
  padding: 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 8px;
}

.first-process-tip {
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
}

.first-process-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.process-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.process-card-wide {
  grid-column: 1 / -1;
}

.process-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.process-fields {
  display: grid;
  gap: 12px;
}

.process-fields-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.field-box {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-box-wide {
  grid-column: 1 / -1;
}

.field-label,
.readonly-label {
  color: #64748b;
  font-size: 12px;
}

.field-number-input {
  width: 100%;
}

.inline-fields {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 8px;
  align-items: center;
}

.part-entry-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 90px;
  gap: 8px;
}

.part-select,
.part-qty-input {
  width: 100%;
}

.part-table {
  width: 100%;
}

.readonly-summary {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-height: 76px;
  padding: 12px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #fff;
}

.actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.upload-box {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  flex-wrap: wrap;
}

.image-link {
  color: #2563eb;
  text-decoration: none;
  font-size: 13px;
}

.image-link:hover {
  text-decoration: underline;
}

.hidden-input {
  display: none;
}

.hint-text,
.money-sub {
  color: #64748b;
  font-size: 12px;
}

.validation-list {
  display: grid;
  gap: 10px;
}

.validation-item {
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;
  font-size: 13px;
}

@media (max-width: 900px) {
  .first-process-grid,
  .process-fields-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .quick-fill-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .part-entry-row,
  .inline-fields {
    grid-template-columns: 1fr;
  }
}
</style>
